import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { BASE_URL } from "../../../../services/configUrls";
import { AppleStyleSpinner } from "./AppleStyleSpinner";

const AddNewFeatureModal = ({
  open,
  onClose,
  newFeatureNameInput,
  setNewFeatureNameInput,
  newFeatureTypeInput,
  setNewFeatureTypeInput,
  onFeatureCreated, // NEW: Callback to refresh AddFeatureModal
}) => {
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (!submitting) {
      setNewFeatureNameInput("");
      setNewFeatureTypeInput("text");
      onClose();
    }
  };

  const handleCreateFeature = async () => {
    if (!newFeatureNameInput.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const featureType =
        newFeatureTypeInput.charAt(0).toUpperCase() +
        newFeatureTypeInput.slice(1);

      const payload = {
        feature_name: newFeatureNameInput.trim(),
        type: featureType,
      };

      const response = await fetch(`${BASE_URL}/accounts/create-feature`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        // Call callback to refresh AddFeatureModal
        if (onFeatureCreated) {
          await onFeatureCreated();
        }

        // Reset form and close modal
        setNewFeatureNameInput("");
        setNewFeatureTypeInput("text");
        onClose();
      } else {
        alert(result.message || "Failed to create feature");
      }
    } catch (error) {
      console.error("Error creating feature:", error);
      alert("Error creating feature. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && newFeatureNameInput.trim() && !submitting) {
      handleCreateFeature();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
      TransitionProps={{
        timeout: 400,
      }}
      PaperProps={{
        sx: {
          animation: open ? "slideUp 0.3s ease-out" : "none",
          "@keyframes slideUp": {
            from: {
              opacity: 0,
              transform: "translateY(20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
        Add New Feature
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          margin="dense"
          label="Feature Name"
          fullWidth
          value={newFeatureNameInput}
          onChange={(e) => setNewFeatureNameInput(e.target.value)}
          placeholder="e.g., Virtual Internship, Technical Workshop"
          onKeyPress={handleKeyPress}
          disabled={submitting}
          sx={{ mb: 2 }}
        />

        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#374151",
              mb: 1.5,
            }}
          >
            Feature Type
          </Typography>
          <RadioGroup
            row
            value={newFeatureTypeInput}
            onChange={(e) => setNewFeatureTypeInput(e.target.value)}
            disabled={submitting}
            sx={{ gap: 3 }}
          >
            <FormControlLabel
              value="text"
              control={
                <Radio
                  size="small"
                  disabled={submitting}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    Text
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.25 }}
                  >
                    For status values like "Eligible", "TBA"
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="count"
              control={
                <Radio
                  size="small"
                  disabled={submitting}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    Count
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.25 }}
                  >
                    For numeric values like "100", "50"
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ fontSize: "13px" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateFeature}
          variant="contained"
          disabled={!newFeatureNameInput.trim() || submitting}
          startIcon={submitting ? <AppleStyleSpinner /> : null}
          sx={{ fontSize: "13px" }}
        >
          {submitting ? "Creating..." : "Add Feature"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewFeatureModal;
